import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import {
    useTheme,
    CircularProgress,
    IconButton,
    Tooltip,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    TablePagination,
    Tabs,
    Tab,
    Menu,
    ListItemIcon,
    ListItemText,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@material-ui/core'
import SyncIcon from '@material-ui/icons/Sync'
import SettingsIcon from '@material-ui/icons/Settings'
import AddIcon from '@material-ui/icons/Add'
import LabelIcon from '@material-ui/icons/Label'
import AppsIcon from '@material-ui/icons/Apps'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import SaveIcon from '@material-ui/icons/Save'
import BusinessIcon from '@material-ui/icons/Business'
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined'
import PeopleOutlinedIcon from '@material-ui/icons/PeopleOutlined'
import AttachMoneyIcon from '@material-ui/icons/AttachMoney'

import { useUser } from '../../../../hooks/useUser'
import { Company, Application } from '../../../../models/Company'
import { TOP_BAR_HEIGHT } from '../../../../navigation/constants'
import { SearchField } from '../../../../components/SearchField'
import { APARTMENT, axiosInstance } from '../../../../helpers'
import { useUserTags } from '../../../../contexts/UserTagsContext'
import { UserTag } from '../../../../models/Users/types'
import { Container } from '../../../../components/Container'
import { PropertyTagsModal } from '../../../../components/PropertyTagsModal/PropertyTagsModal'
import { CompanyApplicationsModal } from '../../../../components/CompanyApplicationsModal/CompanyApplicationsModal'
import { typographyStyle } from '../../../../theme/typographyStyle'
import {
    REQUIRED_APPLICATION_SLUGS,
    ApplicationSlug,
    APPLICATION_SLUGS,
} from '../../../../constants/applications'

function cellKey(companyId: number, applicationId: number): string {
    return `${companyId}-${applicationId}`
}

type SortField = 'name' | 'id' | 'billable_unit_count' | 'address' | 'state'
type Order = 'asc' | 'desc'

function getCompanyAddress(company: Company): string {
    if (company.primary_address?.address1) {
        const { address1, city, state } = company.primary_address
        const parts = [address1, city, state].filter(Boolean)
        return parts.join(', ')
    }
    const parts = [company.address, company.city, company.state].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : 'N/A'
}

/** Sum of (effective_price × billable_unit_count) for all applications; format for display. */
function getCompanyTotalMonthlyFormatted(company: Company): string {
    const applications = company.applications ?? []
    const units = company.billable_unit_count ?? 0
    const total = applications.reduce((sum, app) => {
        const n = parseFloat(app.effective_price ?? '0')
        if (app.slug.toLowerCase() === APPLICATION_SLUGS.EZTURN && n === 0) {
            return sum + parseFloat(company.billable_unit_rate ?? '0') * units
        }
        return sum + (Number.isNaN(n) ? 0 : n) * units
    }, 0)
    return `$${total.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })} per month`
}

export const OrganizationPropertyList = () => {
    const {
        organization,
        actions,
        organizationCompanyList,
        loading,
    } = useUser()
    const theme = useTheme()

    const { userTags, getUserTags } = useUserTags()

    const [isTagsModalOpen, setIsTagsModalOpen] = useState(false)
    const [
        settingsAnchorEl,
        setSettingsAnchorEl,
    ] = useState<null | HTMLElement>(null)
    const [viewMode, setViewMode] = useState<'cards' | 'bulk-edit'>('cards')

    // Bulk edit: applications list and selection (used when viewMode === 'bulk-edit')
    const [applications, setApplications] = useState<Application[]>([])
    const [loadingApps, setLoadingApps] = useState(false)
    const [savingBulkEdit, setSavingBulkEdit] = useState(false)
    const [selection, setSelection] = useState<Record<string, boolean>>({})

    // Function to get all tags for a property (a property can have multiple tags)
    const getTagsForProperty = (propertyId: number): UserTag[] => {
        return userTags.filter((tag) => tag.apartments.includes(propertyId))
    }

    const [isApplicationsModalOpen, setIsApplicationsModalOpen] = useState(
        false,
    )
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null)

    useEffect(() => {
        getUserTags()
    }, [])

    const [orderBy, setOrderBy] = useState<SortField>('name')
    const [order, setOrder] = useState<Order>('asc')
    const [search, setSearch] = useState('')
    const [selectedTagId, setSelectedTagId] = useState<number | null>(null)

    // Add pagination state
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(12)

    // Tab: 0 = Billable Properties, 1 = Other Properties
    const [activeTab, setActiveTab] = useState(0)

    const propertyList = React.useMemo(
        () =>
            organizationCompanyList.filter(
                (company) => company.company_type === APARTMENT,
            ),
        [organizationCompanyList],
    )

    // Handle tag filter change
    const handleTagChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const value = event.target.value
        setSelectedTagId(value === 'all' ? null : (value as number))
    }

    // Pagination handlers
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    // Simple analytics based on the currently selected tab
    const currentTabCompanies = React.useMemo(
        () =>
            propertyList.filter((company) =>
                activeTab === 0
                    ? company.billing_group != null
                    : company.billing_group == null,
            ),
        [propertyList, activeTab],
    )
    const analytics = React.useMemo(() => {
        const totalCompanies = currentTabCompanies.length
        const totalBillableUnits = currentTabCompanies.reduce(
            (sum, c) => sum + (c.billable_unit_count ?? 0),
            0,
        )
        return { totalCompanies, totalBillableUnits }
    }, [currentTabCompanies])

    // Split into billable (has billing_group) and other (no billing_group)
    const billableList = React.useMemo(
        () =>
            propertyList.filter(
                (c) => c.billing_group != null && c.billing_group !== undefined,
            ),
        [propertyList],
    )
    const otherList = React.useMemo(
        () =>
            propertyList.filter(
                (c) => c.billing_group == null || c.billing_group === undefined,
            ),
        [propertyList],
    )

    // Filter + sort for "Other" tab (same logic as sortedCompanies but on otherList)
    const sortedOtherCompanies = React.useMemo(() => {
        let filtered = otherList.filter((company) =>
            company.name.toLowerCase().includes(search.toLowerCase()),
        )
        if (selectedTagId !== null) {
            const selectedTag = userTags.find((tag) => tag.id === selectedTagId)
            if (selectedTag) {
                filtered = filtered.filter((company) =>
                    selectedTag.apartments.includes(company.id),
                )
            }
        }
        return [...filtered].sort((a, b) => {
            if (orderBy === 'name') {
                return order === 'asc'
                    ? a.name.localeCompare(b.name)
                    : b.name.localeCompare(a.name)
            }
            if (orderBy === 'id') {
                return order === 'asc' ? a.id - b.id : b.id - a.id
            }
            if (orderBy === 'billable_unit_count') {
                return order === 'asc'
                    ? a.billable_unit_count - b.billable_unit_count
                    : b.billable_unit_count - a.billable_unit_count
            }
            if (orderBy === 'address') {
                const aAddr = a.primary_address?.address1 || a.address || ''
                const bAddr = b.primary_address?.address1 || b.address || ''
                return order === 'asc'
                    ? aAddr.localeCompare(bAddr)
                    : bAddr.localeCompare(aAddr)
            }
            if (orderBy === 'state') {
                const aState = a.primary_address?.state || a.state || ''
                const bState = b.primary_address?.state || b.state || ''
                return order === 'asc'
                    ? aState.localeCompare(bState)
                    : bState.localeCompare(aState)
            }
            return 0
        })
    }, [otherList, order, orderBy, search, selectedTagId, userTags])

    // For "Billable" tab: filter + sort (same structure as Other tab)
    const sortedBillableCompanies = React.useMemo(() => {
        let filtered = billableList.filter((company) =>
            company.name.toLowerCase().includes(search.toLowerCase()),
        )
        if (selectedTagId !== null) {
            const selectedTag = userTags.find((tag) => tag.id === selectedTagId)
            if (selectedTag) {
                filtered = filtered.filter((company) =>
                    selectedTag.apartments.includes(company.id),
                )
            }
        }
        return [...filtered].sort((a, b) => {
            if (orderBy === 'name') {
                return order === 'asc'
                    ? a.name.localeCompare(b.name)
                    : b.name.localeCompare(a.name)
            }
            if (orderBy === 'id') {
                return order === 'asc' ? a.id - b.id : b.id - a.id
            }
            if (orderBy === 'billable_unit_count') {
                return order === 'asc'
                    ? a.billable_unit_count - b.billable_unit_count
                    : b.billable_unit_count - a.billable_unit_count
            }
            if (orderBy === 'address') {
                const aAddr = a.primary_address?.address1 || a.address || ''
                const bAddr = b.primary_address?.address1 || b.address || ''
                return order === 'asc'
                    ? aAddr.localeCompare(bAddr)
                    : bAddr.localeCompare(aAddr)
            }
            if (orderBy === 'state') {
                const aState = a.primary_address?.state || a.state || ''
                const bState = b.primary_address?.state || b.state || ''
                return order === 'asc'
                    ? aState.localeCompare(bState)
                    : bState.localeCompare(aState)
            }
            return 0
        })
    }, [billableList, order, orderBy, search, selectedTagId, userTags])

    const paginatedBillableCompanies = React.useMemo(() => {
        return sortedBillableCompanies.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage,
        )
    }, [sortedBillableCompanies, page, rowsPerPage])

    const paginatedOtherCompanies = React.useMemo(() => {
        return sortedOtherCompanies.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage,
        )
    }, [sortedOtherCompanies, page, rowsPerPage])

    const handleTabChange = (
        _: React.ChangeEvent<unknown>,
        newValue: number,
    ) => {
        setActiveTab(newValue)
        setPage(0)
    }

    // Companies shown in bulk-edit grid (same as current tab's sorted list = filters + sort apply)
    const bulkEditCompanies = React.useMemo(
        () =>
            activeTab === 0 ? sortedBillableCompanies : sortedOtherCompanies,
        [activeTab, sortedBillableCompanies, sortedOtherCompanies],
    )

    const requiredApplicationIds = React.useMemo(
        () =>
            new Set(
                applications
                    .filter((app) =>
                        REQUIRED_APPLICATION_SLUGS.includes(
                            app.slug.toLowerCase() as ApplicationSlug,
                        ),
                    )
                    .map((app) => app.id),
            ),
        [applications],
    )

    const { toGrant, toRevoke } = React.useMemo(() => {
        const grant: { companyId: number; applicationId: number }[] = []
        const revoke: { companyId: number; applicationId: number }[] = []
        bulkEditCompanies.forEach((company) => {
            const currentIds = new Set(
                (company.applications ?? []).map((a) => a.id),
            )
            applications.forEach((app) => {
                const key = cellKey(company.id, app.id)
                const selected = selection[key] === true
                const had = currentIds.has(app.id)
                if (selected && !had)
                    grant.push({ companyId: company.id, applicationId: app.id })
                if (!selected && had && !requiredApplicationIds.has(app.id)) {
                    revoke.push({
                        companyId: company.id,
                        applicationId: app.id,
                    })
                }
            })
        })
        return { toGrant: grant, toRevoke: revoke }
    }, [bulkEditCompanies, applications, selection, requiredApplicationIds])

    const hasBulkEditChanges = toGrant.length > 0 || toRevoke.length > 0

    useEffect(() => {
        actions.fetchOrganizationCompanyList(organization)
    }, [organization])

    // Fetch applications when entering bulk-edit mode
    useEffect(() => {
        if (viewMode !== 'bulk-edit') return
        let cancelled = false
        setLoadingApps(true)
        axiosInstance
            .get('/beta/api/company/applications/', {
                params: { active_only: true },
            })
            .then((res) => {
                if (!cancelled) setApplications(res.data)
            })
            .catch((err) => {
                console.error(err)
                if (!cancelled) toast.error('Failed to load applications')
            })
            .finally(() => {
                if (!cancelled) setLoadingApps(false)
            })
        return () => {
            cancelled = true
        }
    }, [viewMode])

    // Initialize selection when in bulk-edit and we have companies + applications (re-init when tab/filters change)
    useEffect(() => {
        if (
            viewMode !== 'bulk-edit' ||
            bulkEditCompanies.length === 0 ||
            applications.length === 0
        )
            return
        const initial: Record<string, boolean> = {}
        bulkEditCompanies.forEach((company) => {
            const companyAppIds = new Set(
                (company.applications ?? []).map((a) => a.id),
            )
            applications.forEach((app) => {
                const key = cellKey(company.id, app.id)
                initial[key] = companyAppIds.has(app.id)
            })
        })
        setSelection(initial)
    }, [viewMode, bulkEditCompanies, applications])

    const handleBulkEditToggle = (companyId: number, applicationId: number) => {
        if (requiredApplicationIds.has(applicationId)) return
        const key = cellKey(companyId, applicationId)
        setSelection((prev) => ({ ...prev, [key]: !prev[key] }))
    }

    const isBulkEditChecked = (companyId: number, applicationId: number) =>
        selection[cellKey(companyId, applicationId)] === true

    const handleBulkEditSave = async () => {
        if (!hasBulkEditChanges) {
            setViewMode('cards')
            return
        }
        setSavingBulkEdit(true)
        try {
            for (const { companyId, applicationId } of toGrant) {
                await axiosInstance.post(
                    `/beta/api/company/${companyId}/applications/${applicationId}/`,
                )
            }
            for (const { companyId, applicationId } of toRevoke) {
                await axiosInstance.delete(
                    `/beta/api/company/${companyId}/applications/${applicationId}/`,
                )
            }
            toast.success('Application access updated successfully')
            await actions.fetchOrganizationCompanyList(organization)
            setViewMode('cards')
        } catch (err: any) {
            console.error(err)
            toast.error(err?.message ?? 'Failed to save changes')
        } finally {
            setSavingBulkEdit(false)
        }
    }

    return (
        <Container
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: `calc(100vh - ${TOP_BAR_HEIGHT}px)`,
                padding: theme.spacing(2),
            }}
        >
            {/* Header */}
            <Container
                style={{
                    flexShrink: 0,
                    height: HEADER_HEIGHT,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    alignItems: 'center',
                }}
            >
                <span
                    style={{
                        ...typographyStyle(theme.typography.h6),
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                    }}
                >
                    Property List
                </span>

                <SearchField
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    label="Search"
                    iconColor={theme.palette.text.primary}
                    style={{
                        marginLeft: theme.spacing(2),
                        color: theme.palette.text.primary,
                    }}
                />

                <FormControl
                    variant="outlined"
                    size="small"
                    style={{
                        minWidth: 180,
                        marginLeft: theme.spacing(2),
                        backgroundColor: theme.palette.background.default,
                    }}
                >
                    <InputLabel id="property-tag-label">
                        Property Tag
                    </InputLabel>
                    <Select
                        labelId="property-tag-label"
                        id="property-tag-select"
                        value={selectedTagId ?? 'all'}
                        onChange={handleTagChange}
                        label="Property Tag"
                    >
                        <MenuItem value="all">All Properties</MenuItem>
                        {userTags.map((tag) => (
                            <MenuItem key={tag.id} value={tag.id}>
                                <Box display="flex" alignItems="center">
                                    <Box
                                        style={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            backgroundColor: tag.color,
                                            marginRight: 8,
                                        }}
                                    />
                                    {tag.name}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl
                    variant="outlined"
                    size="small"
                    style={{
                        minWidth: 160,
                        marginLeft: theme.spacing(2),
                    }}
                >
                    <InputLabel>Sort by</InputLabel>
                    <Select
                        value={orderBy}
                        onChange={(e) =>
                            setOrderBy(e.target.value as SortField)
                        }
                        label="Sort by"
                    >
                        <MenuItem value="name">Name</MenuItem>
                        <MenuItem value="billable_unit_count">Units</MenuItem>
                        <MenuItem value="address">Address</MenuItem>
                        <MenuItem value="state">State</MenuItem>
                    </Select>
                </FormControl>

                <IconButton
                    size="small"
                    onClick={() =>
                        setOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
                    }
                    style={{ marginLeft: theme.spacing(0.5) }}
                    title={order === 'asc' ? 'Ascending' : 'Descending'}
                >
                    <Typography variant="caption" color="textSecondary">
                        {order === 'asc' ? 'A→Z' : 'Z→A'}
                    </Typography>
                </IconButton>

                {/* Inline analytics summary with icons */}
                {!loading.organizationCompanyList && (
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(2.5),
                            marginLeft: theme.spacing(2),
                            paddingLeft: theme.spacing(2),
                            borderLeft: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Box
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: theme.spacing(1),
                                gap: theme.spacing(1),
                                padding: theme.spacing(0.75, 1.5),
                                borderRadius: 8,
                                backgroundColor:
                                    theme.palette.grey?.[100] ||
                                    theme.palette.action.hover,
                            }}
                        >
                            <BusinessIcon
                                style={{
                                    fontSize: 20,
                                    color: theme.palette.primary.main,
                                }}
                            />
                            <Typography variant="body2" color="textSecondary">
                                Total properties
                            </Typography>
                            <Typography
                                variant="body1"
                                style={{ fontWeight: 700, minWidth: 24 }}
                            >
                                {analytics.totalCompanies}
                            </Typography>
                        </Box>
                        <Box
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: theme.spacing(1),
                                gap: theme.spacing(1),
                                padding: theme.spacing(0.75, 1.5),
                                borderRadius: 8,
                                backgroundColor:
                                    theme.palette.grey?.[100] ||
                                    theme.palette.action.hover,
                            }}
                        >
                            <PeopleOutlinedIcon
                                style={{
                                    fontSize: 20,
                                    color: theme.palette.text.secondary,
                                }}
                            />
                            <Typography variant="body2" color="textSecondary">
                                Total units
                            </Typography>
                            <Typography
                                variant="body1"
                                style={{ fontWeight: 700, minWidth: 24 }}
                            >
                                {analytics.totalBillableUnits.toLocaleString()}
                            </Typography>
                        </Box>
                    </Box>
                )}

                <div style={{ flex: 1 }} />
                <Tooltip title="Property settings">
                    <IconButton
                        onClick={(e) => setSettingsAnchorEl(e.currentTarget)}
                        aria-controls={
                            settingsAnchorEl
                                ? 'property-settings-menu'
                                : undefined
                        }
                        aria-haspopup="true"
                        aria-expanded={settingsAnchorEl ? 'true' : undefined}
                    >
                        <SettingsIcon />
                    </IconButton>
                </Tooltip>
                <Menu
                    id="property-settings-menu"
                    anchorEl={settingsAnchorEl}
                    open={Boolean(settingsAnchorEl)}
                    onClose={() => setSettingsAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    getContentAnchorEl={null}
                >
                    <MenuItem
                        onClick={() => {
                            setSettingsAnchorEl(null)
                            setIsTagsModalOpen(true)
                        }}
                    >
                        <ListItemIcon>
                            <LabelIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Manage property tags" />
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            setSettingsAnchorEl(null)
                            setViewMode('bulk-edit')
                        }}
                    >
                        <ListItemIcon>
                            <AppsIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Edit property applications" />
                    </MenuItem>
                </Menu>
                <IconButton
                    onClick={() =>
                        actions.fetchOrganizationCompanyList(organization)
                    }
                >
                    <SyncIcon />
                </IconButton>
            </Container>

            {/* Tabs: Billable Properties | Other Properties */}
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                style={{
                    flexShrink: 0,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    minHeight: 48,
                }}
            >
                <Tab label="Billable Properties" id="property-tab-0" />
                <Tab label="Other Properties" id="property-tab-1" />
            </Tabs>

            {/* Tab content */}
            <Container
                style={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Box
                    style={{
                        flexGrow: 1,
                        overflow: 'auto',
                        padding: theme.spacing(2, 0),
                    }}
                >
                    {loading.organizationCompanyList ? (
                        <Container
                            style={{
                                flex: 1,
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                                width: '100%',
                                display: 'flex',
                            }}
                        >
                            <CircularProgress size={100} />
                        </Container>
                    ) : viewMode === 'bulk-edit' ? (
                        /* Bulk edit: grid of properties × applications */
                        <>
                            <Box
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: theme.spacing(2),
                                    marginBottom: theme.spacing(2),
                                    flexShrink: 0,
                                }}
                            >
                                <Button
                                    startIcon={<ArrowBackIcon />}
                                    onClick={() => setViewMode('cards')}
                                    size="small"
                                >
                                    Back to property list
                                </Button>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    Same filters and tab apply. Toggle
                                    checkboxes to grant or revoke application
                                    access.
                                </Typography>
                            </Box>
                            {loadingApps ? (
                                <Box
                                    style={{
                                        flex: 1,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    <CircularProgress size={48} />
                                </Box>
                            ) : (
                                <>
                                    <Box
                                        style={{
                                            flex: 1,
                                            minHeight: 0,
                                            overflow: 'auto',
                                            marginBottom: theme.spacing(2),
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius:
                                                theme.shape.borderRadius,
                                        }}
                                    >
                                        <TableContainer
                                            style={{ maxHeight: '100%' }}
                                        >
                                            <Table
                                                size="small"
                                                stickyHeader
                                                style={{ minWidth: 600 }}
                                            >
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell
                                                            style={{
                                                                fontWeight: 600,
                                                                backgroundColor:
                                                                    theme
                                                                        .palette
                                                                        .background
                                                                        .paper,
                                                                minWidth: 200,
                                                                position:
                                                                    'sticky',
                                                                left: 0,
                                                                zIndex: 2,
                                                                boxShadow:
                                                                    '2px 0 4px rgba(0,0,0,0.08)',
                                                            }}
                                                        >
                                                            Property
                                                        </TableCell>
                                                        {applications.map(
                                                            (app) => (
                                                                <TableCell
                                                                    key={app.id}
                                                                    align="center"
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        whiteSpace:
                                                                            'nowrap',
                                                                        backgroundColor:
                                                                            theme
                                                                                .palette
                                                                                .background
                                                                                .paper,
                                                                    }}
                                                                >
                                                                    {app.name}
                                                                    {requiredApplicationIds.has(
                                                                        app.id,
                                                                    ) && (
                                                                        <Typography
                                                                            component="span"
                                                                            variant="caption"
                                                                            color="textSecondary"
                                                                            style={{
                                                                                display:
                                                                                    'block',
                                                                            }}
                                                                        >
                                                                            (required)
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                            ),
                                                        )}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {bulkEditCompanies.map(
                                                        (company) => (
                                                            <TableRow
                                                                key={company.id}
                                                                hover
                                                            >
                                                                <TableCell
                                                                    component="th"
                                                                    scope="row"
                                                                    style={{
                                                                        fontWeight: 500,
                                                                        position:
                                                                            'sticky',
                                                                        left: 0,
                                                                        backgroundColor:
                                                                            theme
                                                                                .palette
                                                                                .background
                                                                                .paper,
                                                                        zIndex: 1,
                                                                        boxShadow:
                                                                            '2px 0 4px rgba(0,0,0,0.05)',
                                                                    }}
                                                                >
                                                                    {
                                                                        company.name
                                                                    }
                                                                </TableCell>
                                                                {applications.map(
                                                                    (app) => {
                                                                        const checked = isBulkEditChecked(
                                                                            company.id,
                                                                            app.id,
                                                                        )
                                                                        const required = requiredApplicationIds.has(
                                                                            app.id,
                                                                        )
                                                                        return (
                                                                            <TableCell
                                                                                key={
                                                                                    app.id
                                                                                }
                                                                                align="center"
                                                                                padding="checkbox"
                                                                            >
                                                                                <Checkbox
                                                                                    checked={
                                                                                        checked
                                                                                    }
                                                                                    disabled={
                                                                                        required
                                                                                    }
                                                                                    onChange={() =>
                                                                                        handleBulkEditToggle(
                                                                                            company.id,
                                                                                            app.id,
                                                                                        )
                                                                                    }
                                                                                    color="primary"
                                                                                />
                                                                            </TableCell>
                                                                        )
                                                                    },
                                                                )}
                                                            </TableRow>
                                                        ),
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                    <Paper
                                        variant="outlined"
                                        style={{
                                            flexShrink: 0,
                                            padding: theme.spacing(2),
                                            marginBottom: theme.spacing(2),
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            style={{
                                                fontWeight: 600,
                                                marginBottom: theme.spacing(1),
                                            }}
                                        >
                                            Summary of changes
                                        </Typography>
                                        {!hasBulkEditChanges ? (
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                No changes yet. Toggle
                                                checkboxes above to grant or
                                                revoke applications.
                                            </Typography>
                                        ) : (
                                            <Box
                                                component="ul"
                                                style={{
                                                    margin: 0,
                                                    paddingLeft: theme.spacing(
                                                        2,
                                                    ),
                                                }}
                                            >
                                                {toGrant.map(
                                                    ({
                                                        companyId,
                                                        applicationId,
                                                    }) => (
                                                        <li
                                                            key={`g-${companyId}-${applicationId}`}
                                                        >
                                                            <Typography variant="body2">
                                                                <strong>
                                                                    Grant
                                                                </strong>{' '}
                                                                {applications.find(
                                                                    (a) =>
                                                                        a.id ===
                                                                        applicationId,
                                                                )?.name ??
                                                                    applicationId}{' '}
                                                                →{' '}
                                                                {bulkEditCompanies.find(
                                                                    (c) =>
                                                                        c.id ===
                                                                        companyId,
                                                                )?.name ??
                                                                    companyId}
                                                            </Typography>
                                                        </li>
                                                    ),
                                                )}
                                                {toRevoke.map(
                                                    ({
                                                        companyId,
                                                        applicationId,
                                                    }) => (
                                                        <li
                                                            key={`r-${companyId}-${applicationId}`}
                                                        >
                                                            <Typography variant="body2">
                                                                <strong>
                                                                    Revoke
                                                                </strong>{' '}
                                                                {applications.find(
                                                                    (a) =>
                                                                        a.id ===
                                                                        applicationId,
                                                                )?.name ??
                                                                    applicationId}{' '}
                                                                from{' '}
                                                                {bulkEditCompanies.find(
                                                                    (c) =>
                                                                        c.id ===
                                                                        companyId,
                                                                )?.name ??
                                                                    companyId}
                                                            </Typography>
                                                        </li>
                                                    ),
                                                )}
                                            </Box>
                                        )}
                                    </Paper>
                                    <Box
                                        style={{
                                            flexShrink: 0,
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            gap: theme.spacing(1),
                                        }}
                                    >
                                        <Button
                                            onClick={() => setViewMode('cards')}
                                            disabled={savingBulkEdit}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={
                                                savingBulkEdit ? (
                                                    <CircularProgress
                                                        size={16}
                                                        color="inherit"
                                                    />
                                                ) : (
                                                    <SaveIcon />
                                                )
                                            }
                                            onClick={handleBulkEditSave}
                                            disabled={savingBulkEdit}
                                        >
                                            {savingBulkEdit
                                                ? 'Saving...'
                                                : hasBulkEditChanges
                                                ? 'Confirm changes'
                                                : 'Done'}
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </>
                    ) : (
                        /* Cards view */
                        <Grid container spacing={2}>
                            {(activeTab === 0
                                ? paginatedBillableCompanies
                                : paginatedOtherCompanies
                            ).map((company) => (
                                <Grid
                                    item
                                    key={company.id}
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    lg={3}
                                >
                                    <CompanyCard
                                        company={company}
                                        getTagsForProperty={getTagsForProperty}
                                        onManageApps={() => {
                                            setSelectedCompany(company)
                                            setIsApplicationsModalOpen(true)
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>

                {/* Pagination (cards view only) */}
                {!loading.organizationCompanyList && viewMode === 'cards' && (
                    <TablePagination
                        component="div"
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        count={
                            activeTab === 0
                                ? sortedBillableCompanies.length
                                : sortedOtherCompanies.length
                        }
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                        style={{
                            flexShrink: 0,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.paper,
                        }}
                    />
                )}
            </Container>

            <PropertyTagsModal
                open={isTagsModalOpen}
                onClose={() => setIsTagsModalOpen(false)}
                companyList={propertyList}
            />

            <CompanyApplicationsModal
                open={isApplicationsModalOpen}
                onClose={() => {
                    setIsApplicationsModalOpen(false)
                    setSelectedCompany(null)
                }}
                company={selectedCompany}
                onSave={() => {
                    actions.fetchOrganizationCompanyList(organization)
                }}
            />
        </Container>
    )
}

const CompanyCard = ({
    company,
    getTagsForProperty,
    onManageApps,
}: {
    company: Company
    getTagsForProperty: (propertyId: number) => UserTag[]
    onManageApps: () => void
}) => {
    const theme = useTheme()
    const address = getCompanyAddress(company)
    const units = company.billable_unit_count ?? 0
    const applications = company.applications ?? []
    const tags = getTagsForProperty(company.id)

    return (
        <Card
            variant="outlined"
            style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 12,
                borderLeft: `4px solid ${theme.palette.primary.light}`,
                boxShadow: theme.shadows[1],
            }}
        >
            <CardContent
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: theme.spacing(2),
                }}
            >
                {/* Name + icon */}
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        marginBottom: theme.spacing(1),
                    }}
                >
                    <BusinessIcon
                        style={{
                            marginRight: theme.spacing(1),
                            marginTop: 2,
                            color: theme.palette.primary.main,
                            fontSize: 22,
                        }}
                    />
                    <Typography
                        variant="subtitle1"
                        style={{ fontWeight: 600, flex: 1 }}
                    >
                        {company.name}
                    </Typography>
                </Box>

                {/* Tags */}
                <Box
                    style={{
                        marginBottom: theme.spacing(1.5),
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 6,
                    }}
                >
                    {tags.length > 0 ? (
                        tags.map((tag) => (
                            <Chip
                                key={tag.id}
                                label={tag.name}
                                size="small"
                                style={{
                                    backgroundColor: tag.color,
                                    color: 'white',
                                    fontWeight: 500,
                                }}
                            />
                        ))
                    ) : (
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            component="span"
                        >
                            —
                        </Typography>
                    )}
                </Box>

                {/* Address */}
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        marginBottom: theme.spacing(1.5),
                    }}
                >
                    <LocationOnOutlinedIcon
                        style={{
                            marginRight: theme.spacing(1),
                            marginTop: 2,
                            fontSize: 18,
                            color: theme.palette.text.secondary,
                        }}
                    />
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ flex: 1 }}
                    >
                        {address}
                    </Typography>
                </Box>

                {/* Units */}
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: theme.spacing(1, 1.5),
                        borderRadius: 8,
                        backgroundColor:
                            theme.palette.grey?.[100] ||
                            theme.palette.action.hover,
                        marginBottom: theme.spacing(1.5),
                        alignSelf: 'flex-start',
                    }}
                >
                    <PeopleOutlinedIcon
                        style={{
                            marginRight: theme.spacing(1),
                            fontSize: 20,
                            color: theme.palette.text.secondary,
                        }}
                    />
                    <Typography variant="body1" style={{ fontWeight: 600 }}>
                        {units}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginLeft: 4 }}
                    >
                        Units
                    </Typography>
                </Box>

                {/* Active Apps */}
                <Box style={{ marginBottom: theme.spacing(2) }}>
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{
                            display: 'block',
                            marginBottom: theme.spacing(0.5),
                        }}
                    >
                        Active Apps
                    </Typography>
                    {applications.length > 0 ? (
                        <Box
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 6,
                            }}
                        >
                            {applications.map((app) => (
                                <Chip
                                    key={app.id}
                                    label={app.name}
                                    size="small"
                                    variant="outlined"
                                    style={{
                                        backgroundColor:
                                            theme.palette.grey?.[100] ||
                                            theme.palette.action.hover,
                                        border: 'none',
                                        fontSize: '0.75rem',
                                    }}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ fontStyle: 'italic' }}
                        >
                            No apps
                        </Typography>
                    )}
                </Box>

                {/* Total per month */}
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: theme.spacing(1.5),
                        padding: theme.spacing(1.25, 1.5),
                        borderRadius: 8,
                        backgroundColor: theme.palette.primary.light,
                        border: `1px solid ${theme.palette.primary.main}`,
                    }}
                >
                    <AttachMoneyIcon
                        style={{
                            marginRight: theme.spacing(1),
                            color: theme.palette.primary.dark,
                            fontSize: 22,
                        }}
                    />
                    <Typography
                        variant="body1"
                        style={{
                            fontWeight: 700,
                            color: theme.palette.primary.dark,
                        }}
                    >
                        {getCompanyTotalMonthlyFormatted(company)}
                    </Typography>
                </Box>

                {/* Manage Apps button */}
                <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={onManageApps}
                    style={{
                        marginTop: 'auto',
                        borderRadius: 8,
                        padding: theme.spacing(1),
                        backgroundColor:
                            theme.palette.grey?.[100] ||
                            theme.palette.action.hover,
                        borderColor:
                            theme.palette.grey?.[300] || theme.palette.divider,
                        textTransform: 'none',
                        fontWeight: 500,
                    }}
                >
                    Manage Apps
                </Button>
            </CardContent>
        </Card>
    )
}

const HEADER_HEIGHT = 56
